import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faTwitter,
  faInstagram,
  faYoutube,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import defaultLogo from "../../assets/new_logo_circle.png";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { getImageUrl } from "../../utils/getImageUrl";

const Footer = () => {
  const settings = useSiteSettings();
  const logo = settings.logo ? getImageUrl(settings.logo) : defaultLogo;
  const handleLogoError = (event) => {
    if (event.currentTarget.src !== defaultLogo) {
      event.currentTarget.src = defaultLogo;
    }
  };

  const copyrightLine = (settings.copyrightText || '© {CURRENT_YEAR} {SITE_NAME}. All rights reserved.')
    .replace('{CURRENT_YEAR}', new Date().getFullYear())
    .replace('{SITE_NAME}', settings.siteName || 'Jr Institute Of Information Technology');

  return (
    <footer className="bg-custom-pink text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Logo and Social Media */}
          <div className="col-span-1 lg:col-span-1 flex flex-col items-center">
            <img
              src={logo}
              alt="College Logo"
              onError={handleLogoError}
              className="mb-4 h-52" // Adjust negative margin
            />
            {((settings.facebookUrl || settings.twitterUrl || settings.instagramUrl || settings.youtubeUrl || settings.linkedinUrl)) && (
              <div className="flex space-x-4 mb-4 text-md justify-center">
                {settings.facebookUrl && (
                  <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="Facebook">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                )}
                {settings.twitterUrl && (
                  <a href={settings.twitterUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="Twitter">
                    <FontAwesomeIcon icon={faTwitter} />
                  </a>
                )}
                {settings.instagramUrl && (
                  <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="Instagram">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                )}
                {settings.youtubeUrl && (
                  <a href={settings.youtubeUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="YouTube">
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                )}
                {settings.linkedinUrl && (
                  <a href={settings.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="LinkedIn">
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Other sections remain unchanged */}
          <div className="col-span-1 leading-loose">
            <h3 className="text-xl font-bold mb-4 border-l-2 border-gold pl-3">
              Contact Information
            </h3>
            <p>{settings.siteName || 'Jr Institute Of Information Technology'}</p>
            <p>{settings.contactAddress || 'Sakina Area Arusha Tanzania, P. O. Box 7071 Namanga Road'}</p>
            <p>Tel: {settings.contactPhone || '+255 754 360 590'}</p>
            {settings.contactEmail && (
              <p>
                Email:{" "}
                <a
                  href={`mailto:${settings.contactEmail}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {settings.contactEmail}
                </a>
              </p>
            )}
            <p>
              Website:{" "}
              <a
                href="https://www.jriit.ac.tz"
                target="_blank"
                className="hover:text-gold"
                rel="noreferrer"
              >
                https://www.jriit.ac.tz
              </a>
            </p>
          </div>

          {/* Useful Links */}
          <div className="col-span-1 leading-loose">
            <h3 className="text-xl font-bold mb-4 border-l-2 border-gold pl-3">
              Useful Links
            </h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.comptia.org"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  CompTIA
                </a>
              </li>
              <li>
                <a
                  href="https://www.netacad.com/"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  CISCO Networking Academy
                </a>
              </li>
              <li>
                <a
                  href="https://www.eccouncil.org/"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  EC-Council
                </a>
              </li>
              <li>
                <a
                  href="https://www.nactvet.go.tz/"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  NACTVET
                </a>
              </li>
              <li>
                <a
                  href="https://www.tcu.go.tz"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  TCU
                </a>
              </li>
              <li>
                <a
                  href="https://www.moe.go.tz"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  MOE
                </a>
              </li>
              <li>
                <a
                  href="https://www.veta.go.tz"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  VETA
                </a>
              </li>
              <li>
                <a
                  href="https://www.heslb.go.tz"
                  target="_blank"
                  className="hover:text-gold"
                  rel="noreferrer"
                >
                  HESLB
                </a>
              </li>
            </ul>
          </div>

          {/* Admission Info */}
          <div className="col-span-1 leading-loose">
            <h3 className="text-xl font-bold mb-4 border-l-2 border-gold pl-3">
              Admission
            </h3>
            <ul className="space-y-2">
              <li><Link to="/admissions" className="hover:text-gold">Academic Calendar</Link></li>
              <li><Link to="/admissions" className="hover:text-gold">Prospectus</Link></li>
              <li><Link to="/fees" className="hover:text-gold">Fee Structure</Link></li>
              <li><Link to="/course/short-courses" className="hover:text-gold">Short Courses</Link></li>
              <li><Link to="/about/terms-conditions" className="hover:text-gold">Terms & Conditions</Link></li>
            </ul>
          </div>

          {/* Small Map Widget */}
          <div className="col-span-1">
            <h3 className="text-xl font-bold mb-4 border-l-2 border-gold pl-3">
              Where We Are
            </h3>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15931.805561943684!2d36.660178!3d-3.362054!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18371c56dc7ed7ed%3A0xc47ab61515db909e!2sJr%20Institute%20of%20Information%20Technology!5e0!3m2!1ssw!2stz!4v1729119469120!5m2!1ssw!2stz"
              width="100%"
              height="250"
              allowFullScreen=""
              loading="lazy"
              title="Google Map"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>

        {/* Copyright Notice */}
        <div className="border-t border-gray-700 pt-8 text-center">
          <p>{copyrightLine}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
